import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { isSceneChapter, type SceneChapter } from '../lib/chapters.js';

/**
 * Scroll narrative state, derived from the DOM.
 *
 * Pages mark each section with `data-scene-chapter="<id>"`. This provider finds
 * those elements, decides which one owns the viewport, and reports how far
 * through it the visitor has scrolled.
 *
 * Two design choices matter:
 *
 *  - **The DOM is the source of truth, not scroll percentages.** Hardcoding "the
 *    method chapter runs from 30% to 47%" breaks the moment copy changes length.
 *    Measuring the actual sections means the narrative always lines up with what
 *    is on screen, at any viewport height, in any language.
 *  - **Native scrolling is never intercepted.** There is no scroll-jacking, no
 *    virtual scroller and no `preventDefault`. Reverse scrolling therefore works
 *    for free, because progress is a pure function of scroll position.
 *
 * Reads are batched into a single `requestAnimationFrame` and only the active
 * chapter's rectangle is measured per frame, so the cost stays flat regardless
 * of how many chapters a page has.
 */

export interface SceneProgressState {
  /** Progress through the whole page, 0-1. */
  progress: number;
  /** Chapter currently owning the viewport, or null before first measurement. */
  chapter: SceneChapter | null;
  /** Progress through the active chapter, 0-1. */
  chapterProgress: number;
  /** Zero-based index of the active chapter within the page's chapter list. */
  chapterIndex: number;
  /** Chapters found in the document, in order. */
  chapters: readonly SceneChapter[];
  /** Normalised pointer position, −1…1 on each axis. Zero under reduced motion. */
  pointer: { x: number; y: number };
  /** True while the document is hidden, so render loops can pause. */
  documentHidden: boolean;
}

const INITIAL: SceneProgressState = {
  progress: 0,
  chapter: null,
  chapterProgress: 0,
  chapterIndex: 0,
  chapters: [],
  pointer: { x: 0, y: 0 },
  documentHidden: false,
};

const ProgressCtx = createContext<SceneProgressState>(INITIAL);

/**
 * Mutable ref channel for the render loop.
 *
 * Scene meshes must read scroll state at 60 Hz without re-rendering React. They
 * read this ref inside `useFrame`; React consumers read the context state, which
 * updates only when the active chapter changes. Keeping the two separate is what
 * prevents a scroll from triggering a React render on every frame.
 */
const ProgressRefCtx = createContext<{ current: SceneProgressState }>({ current: INITIAL });

export interface SceneProgressProviderProps {
  children: ReactNode;
  /** Disables pointer tracking, e.g. under reduced motion. */
  trackPointer?: boolean;
  /** Element to scope the chapter search to. Defaults to the document. */
  rootRef?: React.RefObject<HTMLElement | null>;
}

export function SceneProgressProvider({
  children,
  trackPointer = true,
  rootRef,
}: SceneProgressProviderProps): JSX.Element {
  const stateRef = useRef<SceneProgressState>(INITIAL);
  // Only the coarse, React-visible slice of state lives here.
  const [coarse, setCoarse] = useState<{
    chapter: SceneChapter | null;
    chapterIndex: number;
    chapters: readonly SceneChapter[];
  }>({ chapter: null, chapterIndex: 0, chapters: [] });

  const [documentHidden, setDocumentHidden] = useState(false);

  useEffect(() => {
    const onVisibility = () => setDocumentHidden(document.visibilityState === 'hidden');
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    stateRef.current = { ...stateRef.current, documentHidden };
  }, [documentHidden]);

  useEffect(() => {
    const root = rootRef?.current ?? document;
    let frame = 0;
    let elements: { chapter: SceneChapter; element: HTMLElement }[] = [];

    function collect(): void {
      const found = Array.from(
        root.querySelectorAll<HTMLElement>('[data-scene-chapter]'),
      ).flatMap((element) => {
        const value = element.dataset.sceneChapter;
        if (!value || !isSceneChapter(value)) return [];
        return [{ chapter: value, element }];
      });

      elements = found;
      const chapters = found.map((entry) => entry.chapter);
      setCoarse((previous) =>
        previous.chapters.length === chapters.length &&
        previous.chapters.every((value, index) => value === chapters[index])
          ? previous
          : { ...previous, chapters },
      );
    }

    function measure(): void {
      frame = 0;
      if (elements.length === 0) return;

      const viewportHeight = window.innerHeight;
      // Anchor on the upper third: a section is "active" once its content, not
      // its top edge, dominates the screen.
      const anchor = viewportHeight * 0.35;

      let activeIndex = 0;
      let activeProgress = 0;

      for (let index = 0; index < elements.length; index += 1) {
        const entry = elements[index];
        if (!entry) continue;
        const rect = entry.element.getBoundingClientRect();
        if (rect.top <= anchor) {
          activeIndex = index;
          const travel = rect.height + viewportHeight * 0.35;
          activeProgress = travel > 0 ? clamp01((anchor - rect.top) / travel) : 0;
        }
      }

      const scrollTop = window.scrollY;
      const scrollable = Math.max(
        1,
        document.documentElement.scrollHeight - viewportHeight,
      );

      const activeChapter = elements[activeIndex]?.chapter ?? null;

      stateRef.current = {
        ...stateRef.current,
        progress: clamp01(scrollTop / scrollable),
        chapter: activeChapter,
        chapterProgress: activeProgress,
        chapterIndex: activeIndex,
        chapters: elements.map((entry) => entry.chapter),
      };

      // Re-render React only when the chapter itself changes.
      setCoarse((previous) =>
        previous.chapter === activeChapter && previous.chapterIndex === activeIndex
          ? previous
          : { ...previous, chapter: activeChapter, chapterIndex: activeIndex },
      );
    }

    function schedule(): void {
      if (frame !== 0) return;
      frame = requestAnimationFrame(measure);
    }

    collect();
    schedule();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    // Sections mount as lazy chunks resolve, so the element list is re-collected
    // when the subtree changes rather than only once.
    const observer = new MutationObserver(() => {
      collect();
      schedule();
    });
    observer.observe(rootRef?.current ?? document.body, { childList: true, subtree: true });

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [rootRef]);

  useEffect(() => {
    if (!trackPointer) {
      stateRef.current = { ...stateRef.current, pointer: { x: 0, y: 0 } };
      return;
    }

    let frame = 0;
    let pending = { x: 0, y: 0 };

    const apply = () => {
      frame = 0;
      stateRef.current = { ...stateRef.current, pointer: pending };
    };

    const onPointerMove = (event: PointerEvent) => {
      // Only a mouse drives parallax. A touch drag is scrolling, not aiming.
      if (event.pointerType !== 'mouse') return;
      pending = {
        x: (event.clientX / window.innerWidth) * 2 - 1,
        y: (event.clientY / window.innerHeight) * 2 - 1,
      };
      if (frame === 0) frame = requestAnimationFrame(apply);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [trackPointer]);

  const contextValue = useMemo<SceneProgressState>(
    () => ({
      ...INITIAL,
      chapter: coarse.chapter,
      chapterIndex: coarse.chapterIndex,
      chapters: coarse.chapters,
      documentHidden,
    }),
    [coarse, documentHidden],
  );

  return (
    <ProgressRefCtx.Provider value={stateRef}>
      <ProgressCtx.Provider value={contextValue}>{children}</ProgressCtx.Provider>
    </ProgressRefCtx.Provider>
  );
}

/**
 * Coarse scroll state for HTML. Re-renders only when the active chapter changes,
 * which is what makes it safe to call from a page component.
 */
export function useSceneProgress(): SceneProgressState {
  return useContext(ProgressCtx);
}

/**
 * High-frequency scroll state for `useFrame`. Never triggers a React render -
 * read `.current` inside the loop.
 */
export function useSceneProgressRef(): { current: SceneProgressState } {
  return useContext(ProgressRefCtx);
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
